import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, Type, AlignLeft, ChevronUp, ChevronDown, Asterisk } from 'lucide-react';

/**
 * FormFieldsBuilder - Admin UI for creating custom form fields
 * Supports Text Input and Text Area field types
 * Each field has: label, placeholder (optional), required checkbox
 */
const FormFieldsBuilder = ({ fields = [], onChange, onContentChange }) => {
    const [draggedIndex, setDraggedIndex] = useState(null);

    // Generate unique ID for new fields
    const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Add a new field
    const addField = (fieldType) => {
        const newField = {
            id: generateFieldId(),
            type: fieldType, // 'text_input' or 'text_area'
            label: '',
            placeholder: '',
            required: false
        };

        const updated = [...fields, newField];
        onChange(updated);
        onContentChange?.('form_fields', `Added ${fieldType === 'text_input' ? 'Text Input' : 'Text Area'} field`);
    };

    // Update a specific field
    const updateField = (index, key, value) => {
        const updated = [...fields];
        updated[index] = { ...updated[index], [key]: value };
        onChange(updated);
    };

    // Remove a field
    const removeField = (index) => {
        const fieldLabel = fields[index]?.label || `Field ${index + 1}`;
        const updated = fields.filter((_, i) => i !== index);
        onChange(updated);
        onContentChange?.('form_fields', `Removed field: ${fieldLabel}`);
    };

    // Move field up or down
    const moveField = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < fields.length) {
            const updated = [...fields];
            [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
            onChange(updated);
        }
    };

    // Get field type icon
    const getFieldIcon = (type) => {
        switch (type) {
            case 'text_input':
                return <Type className="w-4 h-4" />;
            case 'text_area':
                return <AlignLeft className="w-4 h-4" />;
            default:
                return <Type className="w-4 h-4" />;
        }
    };

    // Get field type label
    const getFieldTypeLabel = (type) => {
        switch (type) {
            case 'text_input':
                return 'Text Input';
            case 'text_area':
                return 'Text Area';
            default:
                return 'Field';
        }
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-200">Form Fields</h4>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Add custom input fields for employees to fill out</p>
                </div>
            </div>

            {/* Field List */}
            {fields.length === 0 ? (
                <div className="bg-gray-50 dark:bg-slate-800/50 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Type className="w-6 h-6 text-gray-400 dark:text-slate-500" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-2">No form fields yet</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500">Click the buttons below to add input fields</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {fields.map((field, index) => (
                        <div
                            key={field.id}
                            className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:border-gray-300 dark:border-slate-600 transition-colors"
                        >
                            {/* Field Header */}
                            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100 dark:border-slate-700/50">
                                {/* Reorder Buttons */}
                                <div className="flex flex-col gap-0.5">
                                    <button
                                        type="button"
                                        onClick={() => moveField(index, 'up')}
                                        disabled={index === 0}
                                        className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Move up"
                                    >
                                        <ChevronUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveField(index, 'down')}
                                        disabled={index === fields.length - 1}
                                        className="p-0.5 text-gray-400 hover:text-gray-600 dark:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Move down"
                                    >
                                        <ChevronDown className="w-3.5 h-3.5" />
                                    </button>
                                </div>

                                {/* Field Type Badge */}
                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${
                                    field.type === 'text_input' 
                                        ? 'bg-blue-50 text-blue-700' 
                                        : 'bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400'
                                }`}>
                                    {getFieldIcon(field.type)}
                                    <span>{getFieldTypeLabel(field.type)}</span>
                                </div>

                                {/* Required Badge */}
                                {field.required && (
                                    <span className="flex items-center gap-1 px-2 py-1 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-md text-xs font-medium">
                                        <Asterisk className="w-3 h-3" />
                                        Required
                                    </span>
                                )}

                                {/* Spacer */}
                                <div className="flex-1" />

                                {/* Delete Button */}
                                <button
                                    type="button"
                                    onClick={() => removeField(index)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 dark:bg-red-500/10 rounded-md transition-colors"
                                    title="Remove field"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Field Configuration */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Label Input */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">
                                        Field Label <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={field.label}
                                        onChange={(e) => updateField(index, 'label', e.target.value)}
                                        placeholder="e.g., Full Name, Comments..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                </div>

                                {/* Placeholder Input */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">
                                        Placeholder <span className="text-gray-400 dark:text-slate-500">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={field.placeholder}
                                        onChange={(e) => updateField(index, 'placeholder', e.target.value)}
                                        placeholder="e.g., Enter your full name..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                </div>

                                {/* Required Checkbox */}
                                <div className="md:col-span-2">
                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={field.required}
                                            onChange={(e) => updateField(index, 'required', e.target.checked)}
                                            className="w-4 h-4 text-primary rounded border-gray-300 dark:border-slate-600 focus:ring-primary"
                                        />
                                        <span className="text-sm text-gray-700 dark:text-slate-200">This field is required</span>
                                    </label>
                                </div>
                            </div>

                            {/* Preview */}
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-700/50">
                                <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">Preview:</p>
                                <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-3">
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1.5">
                                        {field.label || 'Field Label'}
                                        {field.required && <span className="text-red-500 ml-1">*</span>}
                                    </label>
                                    {field.type === 'text_input' ? (
                                        <input
                                            type="text"
                                            placeholder={field.placeholder || 'Enter text...'}
                                            disabled
                                            className="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900 text-gray-400 dark:text-slate-500"
                                        />
                                    ) : (
                                        <textarea
                                            placeholder={field.placeholder || 'Enter text...'}
                                            disabled
                                            rows="3"
                                            className="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900 text-gray-400 dark:text-slate-500 resize-none"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Field Buttons */}
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() => addField('text_input')}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-medium text-sm transition-colors border border-blue-200"
                >
                    <Type className="w-4 h-4" />
                    Add Text Input
                </button>
                <button
                    type="button"
                    onClick={() => addField('text_area')}
                    className="flex items-center gap-2 px-4 py-2.5 bg-purple-50 dark:bg-purple-500/10 hover:bg-purple-100 text-purple-700 dark:text-purple-400 rounded-lg font-medium text-sm transition-colors border border-purple-200"
                >
                    <AlignLeft className="w-4 h-4" />
                    Add Text Area
                </button>
            </div>

            {/* Summary */}
            {fields.length > 0 && (
                <div className="bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-lg p-3">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-slate-300">
                            <span className="font-medium">{fields.length}</span> field{fields.length !== 1 ? 's' : ''} added
                        </span>
                        <span className="text-gray-600 dark:text-slate-300">
                            <span className="font-medium text-red-600 dark:text-red-400">{fields.filter(f => f.required).length}</span> required
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormFieldsBuilder;
